<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import whatsappChannel from 'dashboard/api/channel/whatsappChannel';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inboxId: { type: Number, default: null },
});
const emit = defineEmits(['connected']);
const { t } = useI18n();
const { run, abort } = useAbortableRequest();
const POLL_INTERVAL_MS = 3000;
const CONNECTED_POLL_INTERVAL_MS = 15000;
const activeInboxId = ref(props.inboxId);
const inboxName = ref('');
const session = ref({ status: 'STOPPED' });
const isBusy = ref(false);
const hasError = ref(false);
let pollTimer = null;
let active = true;

const statusLabel = computed(() => {
  const labels = {
    STOPPED: t('INBOX_MGMT.WAHA.STATUS.STOPPED'),
    STARTING: t('INBOX_MGMT.WAHA.STATUS.STARTING'),
    SCAN_QR_CODE: t('INBOX_MGMT.WAHA.STATUS.SCAN_QR_CODE'),
    WORKING: t('INBOX_MGMT.WAHA.STATUS.WORKING'),
    FAILED: t('INBOX_MGMT.WAHA.STATUS.FAILED'),
  };
  return labels[session.value.status] || labels.FAILED;
});
const isConnected = computed(() => session.value.status === 'WORKING');

function stopPolling() {
  clearTimeout(pollTimer);
  abort();
}

async function poll() {
  try {
    const response = await run(signal =>
      whatsappChannel.getWahaSession(activeInboxId.value, { signal })
    );
    if (!response || !active) return;
    const previouslyConnected = isConnected.value;
    session.value = response.data;
    hasError.value = false;
    if (isConnected.value && !previouslyConnected) {
      emit('connected', { inbox_id: activeInboxId.value });
    }
    if (
      ['STARTING', 'SCAN_QR_CODE', 'WORKING'].includes(session.value.status)
    ) {
      pollTimer = setTimeout(
        poll,
        isConnected.value ? CONNECTED_POLL_INTERVAL_MS : POLL_INTERVAL_MS
      );
    }
  } catch {
    hasError.value = true;
    session.value = { status: 'FAILED' };
  }
}

async function updateSession(action = 'connect') {
  stopPolling();
  isBusy.value = true;
  hasError.value = false;
  try {
    if (!activeInboxId.value) {
      const response = await whatsappChannel.createWahaInbox(
        inboxName.value.trim()
      );
      activeInboxId.value = response.data.inbox_id;
    }
    await whatsappChannel.updateWahaSession(activeInboxId.value, action);
    if (active) await poll();
  } catch {
    hasError.value = true;
    session.value = { status: 'FAILED' };
  } finally {
    isBusy.value = false;
  }
}

onMounted(() => {
  if (activeInboxId.value) poll();
});
onBeforeUnmount(() => {
  active = false;
  stopPolling();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-lg font-medium text-n-slate-12">
      {{ $t('INBOX_MGMT.WAHA.TITLE') }}
    </h3>
    <p class="text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.QR_SCREEN.INSTRUCTIONS') }}
    </p>
    <woot-input
      v-if="!activeInboxId"
      v-model="inboxName"
      :label="$t('INBOX_MGMT.ADD.CHANNEL_NAME.LABEL')"
      :placeholder="$t('INBOX_MGMT.ADD.CHANNEL_NAME.PLACEHOLDER')"
    />
    <p v-if="hasError" class="text-sm text-n-ruby-11" role="alert">
      {{ $t('INBOX_MGMT.WAHA.ERROR') }}
    </p>
    <div v-else class="flex items-center gap-2 text-sm text-n-slate-11">
      <Spinner v-if="isBusy || session.status === 'STARTING'" class="size-4" />
      <span v-if="isConnected" class="i-lucide-circle-check text-n-teal-11" />
      {{ statusLabel }}
      <span v-if="session.phone_number" dir="ltr">
        {{ session.phone_number }}
      </span>
    </div>
    <div v-if="session.qr" class="flex justify-start">
      <div class="p-3 bg-white rounded-xl border border-n-weak">
        <img
          :src="session.qr"
          :alt="$t('INBOX_MGMT.ADD.WHATSAPP.QR_SCREEN.TITLE')"
          class="size-56"
        />
      </div>
    </div>
    <div class="flex flex-wrap gap-2">
      <Button
        v-if="
          !activeInboxId ||
          hasError ||
          ['STOPPED', 'FAILED'].includes(session.status)
        "
        :label="$t('INBOX_MGMT.WAHA.CONNECT')"
        icon="i-lucide-qr-code"
        :disabled="isBusy || (!activeInboxId && !inboxName.trim())"
        @click="updateSession()"
      />
      <Button
        v-if="activeInboxId"
        :label="$t('INBOX_MGMT.WAHA.REFRESH')"
        :disabled="isBusy"
        @click="
          stopPolling();
          poll();
        "
      />
      <Button
        v-if="isConnected"
        :label="$t('INBOX_MGMT.WAHA.RESTART')"
        :disabled="isBusy"
        @click="updateSession('restart')"
      />
      <Button
        v-if="isConnected"
        :label="$t('INBOX_MGMT.WAHA.LOGOUT')"
        :disabled="isBusy"
        @click="updateSession('logout')"
      />
    </div>
  </div>
</template>
